/**
 * SERVIÇO DE APLICAÇÃO — IMPORTAÇÃO EM LOTE DE EQUIPAMENTOS DE END
 *
 * Orquestra a ingestão de planilhas (.xlsx multi-aba e .csv mono-aba),
 * validação de esquema, enriquecimento com certificados de calibração,
 * comparação com a base de dados (staging / diff) e execução transacional
 * via NdtAssetService com preservação não-destrutiva de histórico.
 */

import type { RequestContext } from "../context/request-context.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { NdtAssetService } from "./ndt-asset-service.js";
import type { NdtAsset, NdtAssetType } from "../../domain/ndt/ndt-assets.js";
import type { NdtAssetCertificate } from "../../domain/ndt/ndt-asset-history.js";
import {
  type NdtImportRowDraft,
  type NdtImportIssue,
  type NdtImportStagedItem,
  type NdtImportSummary,
  type NdtImportExecutionResult,
  resolveSheetAssetType,
  parseImportRow,
  matchAssetWithDatabase,
  classifyStagedRow,
  buildNdtEquipmentXlsxTemplate,
  buildNdtEquipmentCsvTemplate,
  NDT_EQUIPMENT_SHEET_MAPPINGS
} from "../../domain/ndt/ndt-equipment-import.js";
import { readWorkbook, SpreadsheetFormatError } from "../../infrastructure/import/xlsx-reader.js";

export class NdtEquipmentImportService {
  constructor(private readonly assetService: NdtAssetService) {}

  /**
   * Faz o parsing completo de um arquivo .xlsx com múltiplas abas de equipamentos.
   */
  async parseWorkbookBytes(rawBytes: unknown): Promise<{ drafts: NdtImportRowDraft[]; issues: NdtImportIssue[] }> {
    let workbook;
    try {
      workbook = await readWorkbook(rawBytes);
    } catch (err: any) {
      if (err instanceof SpreadsheetFormatError || err instanceof AppError) {
        throw (err instanceof AppError ? err : new AppError(ErrorCode.Validation, err.message));
      }
      throw new AppError(ErrorCode.Validation, `Falha ao processar arquivo XLSX: ${err?.message || "Arquivo inválido"}`);
    }

    const drafts: NdtImportRowDraft[] = [];
    const issues: NdtImportIssue[] = [];
    let recognizedSheetsCount = 0;

    for (const sheetName of workbook.sheetNames) {
      const assetType = resolveSheetAssetType(sheetName);
      if (!assetType) {
        // Aba não pertence aos 5 tipos físicos de END (ex: Procedimentos, Normas, etc. ou aba auxiliar)
        continue;
      }

      recognizedSheetsCount++;
      const table = workbook.table(sheetName);
      if (!table || table.rows.length === 0) continue;

      for (const row of table.rows) {
        const { draft, issues: rowIssues } = parseImportRow(
          row.rowNumber,
          sheetName,
          assetType,
          row.values as Record<string, string>
        );

        drafts.push(draft);
        if (rowIssues.length > 0) {
          issues.push(...rowIssues);
        }
      }
    }

    if (recognizedSheetsCount === 0) {
      issues.push({
        rowNumber: 0,
        severity: "ERROR",
        message: "Nenhuma aba correspondente a equipamentos de END foi identificada na planilha. Utilize o modelo padrão."
      });
    }

    return { drafts, issues };
  }

  /**
   * Faz o parsing de um conteúdo de texto CSV voltado para um tipo específico de equipamento.
   */
  parseCsvText(csvText: string, targetAssetType: NdtAssetType): { drafts: NdtImportRowDraft[]; issues: NdtImportIssue[] } {
    const drafts: NdtImportRowDraft[] = [];
    const issues: NdtImportIssue[] = [];

    const lines = csvText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      issues.push({
        rowNumber: 0,
        severity: "ERROR",
        message: "O arquivo CSV deve conter ao menos a linha de cabeçalho e uma linha de dados."
      });
      return { drafts, issues };
    }

    // Detecta delimitador (; ou , ou tab)
    const headerLine = lines[0]!;
    let delimiter = ";";
    if (headerLine.includes(";") && !headerLine.includes("\t")) delimiter = ";";
    else if (headerLine.includes(",") && !headerLine.includes(";")) delimiter = ",";
    else if (headerLine.includes("\t")) delimiter = "\t";

    const parseCsvLine = (line: string): string[] => {
      const result: string[] = [];
      let cur = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === delimiter && !inQuotes) {
          result.push(cur.trim());
          cur = "";
        } else {
          cur += char;
        }
      }
      result.push(cur.trim());
      return result;
    };

    const headers = parseCsvLine(headerLine);
    const sheetName = NDT_EQUIPMENT_SHEET_MAPPINGS.find(m => m.assetType === targetAssetType)?.defaultSheetName || targetAssetType;

    for (let i = 1; i < lines.length; i++) {
      const rowNumber = i + 1;
      const valuesArray = parseCsvLine(lines[i]!);
      const rawValues: Record<string, string> = {};

      headers.forEach((h, idx) => {
        if (h) {
          rawValues[h] = valuesArray[idx] ?? "";
        }
      });

      const { draft, issues: rowIssues } = parseImportRow(rowNumber, sheetName, targetAssetType, rawValues);
      drafts.push(draft);
      if (rowIssues.length > 0) {
        issues.push(...rowIssues);
      }
    }

    return { drafts, issues };
  }

  /**
   * Compara os rascunhos com a base existente e gera a prévia/staging com contadores e classificação.
   */
  async stageImport(
    context: RequestContext,
    drafts: readonly NdtImportRowDraft[],
    initialIssues: readonly NdtImportIssue[] = []
  ): Promise<{
    stagedItems: NdtImportStagedItem[];
    summary: NdtImportSummary;
    issues: NdtImportIssue[];
  }> {
    // 1. Busca todos os ativos existentes no cliente ativo
    const allExistingAssets = await this.assetService.listAssets(context, undefined, true);

    // 2. Busca histórico de certificados para cada ativo
    const certificatesMap = new Map<string, NdtAssetCertificate[]>();
    await Promise.all(
      allExistingAssets.map(async asset => {
        const certs = await this.assetService.listCertificates(context, asset.id);
        certificatesMap.set(asset.id, certs);
      })
    );

    const stagedItems: NdtImportStagedItem[] = [];
    const allIssues: NdtImportIssue[] = [...initialIssues];

    // Detecção de duplicidade dentro do próprio arquivo de importação
    const seenKeysInFile = new Map<string, number>(); // key -> first rowNumber

    for (const draft of drafts) {
      const rowIssues = allIssues.filter(i => i.rowNumber === draft.rowNumber && i.severity === "ERROR");

      // Deduplicação intra-arquivo
      const fileKey = `${draft.assetType}::${draft.serialNumber || draft.internalCode}`.toLowerCase();
      if (draft.serialNumber || draft.internalCode) {
        if (seenKeysInFile.has(fileKey)) {
          const prevRow = seenKeysInFile.get(fileKey)!;
          rowIssues.push({
            rowNumber: draft.rowNumber,
            severity: "ERROR",
            message: `Registro duplicado dentro da própria planilha (mesmo identificador da linha ${prevRow}).`
          });
          allIssues.push(rowIssues[rowIssues.length - 1]!);
        } else {
          seenKeysInFile.set(fileKey, draft.rowNumber);
        }
      }

      // Match com base de dados
      const existingAsset = matchAssetWithDatabase(draft, allExistingAssets);
      const existingCerts = existingAsset ? (certificatesMap.get(existingAsset.id) || []) : [];

      const staged = classifyStagedRow(draft, rowIssues, existingAsset, existingCerts);
      stagedItems.push(staged);
    }

    // Calcula sumário consolidado
    let countNew = 0;
    let countUpdate = 0;
    let countNewCalibration = 0;
    let countUnchanged = 0;
    let totalErrors = 0;
    let totalWarnings = 0;

    const bySheet: Record<string, { sheetName: string; assetType: NdtAssetType; total: number; valid: number; errors: number }> = {};

    for (const item of stagedItems) {
      const sheetKey = item.sheetName;
      if (!bySheet[sheetKey]) {
        bySheet[sheetKey] = {
          sheetName: item.sheetName,
          assetType: item.assetType,
          total: 0,
          valid: 0,
          errors: 0
        };
      }
      bySheet[sheetKey]!.total++;

      if (item.status === "ERROR" || item.isBlocked) {
        totalErrors++;
        bySheet[sheetKey]!.errors++;
      } else {
        bySheet[sheetKey]!.valid++;
        if (item.status === "NEW") countNew++;
        else if (item.status === "UPDATE") countUpdate++;
        else if (item.status === "NEW_CALIBRATION") countNewCalibration++;
        else if (item.status === "UNCHANGED") countUnchanged++;
      }

      if (item.issues.some(i => i.severity === "WARNING")) {
        totalWarnings++;
      }
    }

    const summary: NdtImportSummary = {
      totalRows: stagedItems.length,
      totalValid: stagedItems.length - totalErrors,
      totalErrors,
      totalWarnings,
      countNew,
      countUpdate,
      countNewCalibration,
      countUnchanged,
      bySheet
    };

    return { stagedItems, summary, issues: allIssues };
  }

  /**
   * Executa a gravação efetiva do lote no banco de dados.
   * Tolerante a falhas parciais: processa item a item e reporta resultados detalhados.
   */
  async executeBatchImport(
    context: RequestContext,
    stagedItems: readonly NdtImportStagedItem[]
  ): Promise<NdtImportExecutionResult> {
    let createdCount = 0;
    let updatedCount = 0;
    let newCertificatesCount = 0;
    let unchangedCount = 0;
    let skippedErrorsCount = 0;
    const errors: Array<{ rowNumber: number; sheetName: string; message: string }> = [];
    const createdAssetIds: string[] = [];
    const updatedAssetIds: string[] = [];

    for (const item of stagedItems) {
      if (item.isBlocked || item.status === "ERROR") {
        skippedErrorsCount++;
        errors.push({
          rowNumber: item.rowNumber,
          sheetName: item.sheetName,
          message: item.issues.map(i => i.message).join(" | ") || "Linha com erro de validação."
        });
        continue;
      }

      if (item.status === "UNCHANGED") {
        unchangedCount++;
        continue;
      }

      try {
        const draft = item.draft;

        if (item.status === "NEW") {
          // Cria o equipamento no domínio
          const assetData: any = {
            assetType: draft.assetType,
            internalCode: draft.internalCode,
            serialNumber: draft.serialNumber,
            manufacturer: draft.manufacturer,
            model: draft.model,
            description: draft.description,
            technicalData: draft.technicalData
          };
          if (draft.patrimonyNumber) assetData.patrimonyNumber = draft.patrimonyNumber;
          if (draft.notes) assetData.notes = draft.notes;

          if (draft.steps && draft.assetType === "CALIBRATION_BLOCK") {
            assetData.steps = draft.steps;
          }

          const created = await this.assetService.createAsset(context, assetData);
          createdCount++;
          createdAssetIds.push(created.id);

          // Se tiver certificado, vincula
          if (draft.certificate && draft.certificate.certificateNumber && draft.certificate.certificateNumber !== "NÃO INFORMADO") {
            const certData: any = { certificateNumber: draft.certificate.certificateNumber };
            if (draft.certificate.issueDate) certData.issueDate = draft.certificate.issueDate;
            if (draft.certificate.expirationDate) certData.expirationDate = draft.certificate.expirationDate;
            if (draft.certificate.laboratory) certData.laboratory = draft.certificate.laboratory;
            await this.assetService.addCertificate(context, created.id, certData);
            newCertificatesCount++;
          }

        } else if (item.status === "UPDATE" || item.status === "NEW_CALIBRATION") {
          const existingAsset = item.existingAsset!;

          // Atualiza dados cadastrais se necessário
          const updates: Partial<NdtAsset> = {};
          if (draft.manufacturer && draft.manufacturer !== "NÃO INFORMADO") updates.manufacturer = draft.manufacturer;
          if (draft.model && draft.model !== "NÃO INFORMADO") updates.model = draft.model;
          if (draft.description && !draft.description.startsWith(draft.assetType)) updates.description = draft.description;
          if (draft.patrimonyNumber) updates.patrimonyNumber = draft.patrimonyNumber;
          if (draft.notes) updates.notes = draft.notes;
          if (Object.keys(draft.technicalData).length > 0) {
            updates.technicalData = { ...existingAsset.technicalData, ...draft.technicalData };
          }
          if (draft.steps && draft.assetType === "CALIBRATION_BLOCK") {
            (updates as any).steps = draft.steps;
          }

          if (Object.keys(updates).length > 0) {
            await this.assetService.updateAsset(context, existingAsset.id, updates);
            updatedCount++;
            updatedAssetIds.push(existingAsset.id);
          }

          // Se for nova calibração, apêndice histórico não destrutivo
          if (draft.certificate && draft.certificate.certificateNumber && draft.certificate.certificateNumber !== "NÃO INFORMADO") {
            const certData: any = { certificateNumber: draft.certificate.certificateNumber };
            if (draft.certificate.issueDate) certData.issueDate = draft.certificate.issueDate;
            if (draft.certificate.expirationDate) certData.expirationDate = draft.certificate.expirationDate;
            if (draft.certificate.laboratory) certData.laboratory = draft.certificate.laboratory;
            await this.assetService.addCertificate(context, existingAsset.id, certData);
            newCertificatesCount++;
          }
        }
      } catch (err: any) {
        skippedErrorsCount++;
        errors.push({
          rowNumber: item.rowNumber,
          sheetName: item.sheetName,
          message: err?.message || "Erro inesperado ao persistir registro."
        });
      }
    }

    return {
      success: errors.length === 0,
      totalProcessed: stagedItems.length,
      createdCount,
      updatedCount,
      newCertificatesCount,
      unchangedCount,
      skippedErrorsCount,
      errors,
      createdAssetIds,
      updatedAssetIds
    };
  }

  /**
   * Gera a planilha modelo em formato XLSX (5 abas) ou CSV (1 aba).
   */
  generateTemplate(format: "xlsx" | "csv", targetAssetType?: NdtAssetType): Uint8Array | string {
    if (format === "csv") {
      return buildNdtEquipmentCsvTemplate(targetAssetType || "ULTRASONIC_THICKNESS_GAUGE");
    }
    return buildNdtEquipmentXlsxTemplate();
  }
}
